import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Row, Col, InputNumber } from 'antd'
import TracPatchOverrideForm from './tracpatch.override.form'
import KneeROMFigure from './knee.rom.figure'
import ImageSwapFigure from '../exercise/ImageSwapFigure'
import { assetKindForExercise } from '../../business/exercise/exerciseAssetMap'
import { resolveWeekCell, currentWeekFromSurgery } from './tracpatch.week.defaults'

// Mirrors physio_app_web/components/tracpatch_exercise/TracPatchSession.js
// deriveBands(). Kept inline — physio_admin doesn't depend on physio_app_web.
// Unified formula for every exercise mode (canonical chain ext_limit ≤
// ext_high ≤ ext_low ≤ flex_low ≤ flex_high ≤ flex_limit):
//   green  = [extension_limit, flexion_limit]
//   yellow = [extension_high,  flexion_high]
//   blue   = [extension_low,   flexion_low]
function deriveBands(cfg) {
  return {
    green:  [cfg.extension_limit, cfg.flexion_limit],
    yellow: [cfg.extension_high,  cfg.flexion_high],
    blue:   [cfg.extension_low,   cfg.flexion_low],
  }
}

/**
 * Modal for editing a TracPatch exercise's configuration.
 *
 * Left pane: schedule inputs (hold/repeat/time/set) + per-patient overrides.
 * Right pane: live preview that mirrors the patient-facing exercise view
 * (`physio_app_web/pages/tracpatch-exercise-debug.js`) — extension/flexion/
 * range tabs, the KneeROMFigure with derived bands, and the action buttons —
 * driven by the values currently in the form so the clinician sees what the
 * patient will see.
 */
const TracPatchConfigModal = (props) => {
  // Pin time=1 on entry — TracPatch exercises are single-slot system-wide.
  // Even if the source row has time>1 (e.g. legacy data), the modal opens
  // it as 1 and any save below uses 1.
  const [input, setInput] = useState(
    Object.assign({}, props.initialInput, { time: 1 })
  )
  const [overrides, setOverrides] = useState(props.initialOverrides || {})
  // Set by the override form whenever its inputs are all present and in order.
  // Gates the Save button so invalid / empty configs can't be persisted.
  const [formValid, setFormValid] = useState(true)

  // Plain setter for schedule-entry fields (`set`, etc.). Hold / Repeat used
  // to live in a dedicated SCHEDULE card and were kept in lock-step with the
  // override form's Hold (s) / Required reps; that card is gone now and reps
  // is per-week, so the lockstep collapses to a one-way mirror from overrides
  // → input in handleOverridesChange below.
  const setField = (k, v) => {
    setInput((prev) => ({ ...prev, [k]: v }))
  }

  const handleOverridesChange = (next) => {
    setOverrides(next)
    // Keep the schedule entry's `hold` column in sync with the override
    // form's value so downstream schedule consumers see what the clinician
    // set in TARGETS. Reps are NOT mirrored any more: the schedule row's
    // REPEAT is the care plan's value and is edited as itself above the
    // form (2026-10-16); the form's Reps column shows it read-only.
    if (
      next.requiredHoldSeconds != null &&
      next.requiredHoldSeconds !== input.hold
    ) {
      setInput((prev) => ({
        ...prev,
        hold: next.requiredHoldSeconds,
        holdPostfix: 'second',
      }))
    }
  }

  // Seed BOTH directions on mount so SCHEDULE row and override form are
  // always in lock-step:
  //   • input.repeat / input.hold → overrides.requiredReps /
  //     requiredHoldSeconds (so override form picks up the schedule's
  //     existing values when overrides was empty)
  //   • overrides.requiredReps / requiredHoldSeconds → input.repeat /
  //     input.hold (so the schedule row reflects a master-template hold
  //     of e.g. 2s when the schedule field is blank)
  // Master-template values flow in through `resolved()` → `v()` in the
  // override form, so they'll be available in `overrides` once the
  // outer modal seeds them — but the second direction here covers the
  // path where the schedule fields themselves arrive empty.
  useEffect(() => {
    const seedOverrides = Object.assign({}, overrides)
    const seedInput = Object.assign({}, input)
    let overridesChanged = false
    let inputChanged = false

    // input → overrides
    // NOTE: do NOT seed overrides.requiredReps from the schedule REPEAT. Reps
    // are per-week (rep_milestone_by_week); a flat requiredReps makes
    // resolveWeekCell return that number for EVERY week, flattening the clinic/
    // tier per-week rep milestones. A flat requiredReps is only set explicitly
    // (at add time when the admin changes REPEAT) and is preserved as-is.
    if (seedOverrides.requiredHoldSeconds == null && (input.hold || 0) > 0) {
      const holdNum = parseInt(input.hold, 10) || 0
      const postfix = input.holdPostfix || 'second'
      const holdSec = postfix === 'minute' ? holdNum * 60 : holdNum
      if (holdSec > 0) {
        seedOverrides.requiredHoldSeconds = holdSec
        seedOverrides.mode = seedOverrides.mode || 'hold'
        overridesChanged = true
      }
    }

    // overrides → input (fills blank schedule fields from existing
    // override / master-template values so HOLD and Hold(s) read the
    // same number).
    if ((parseInt(input.repeat, 10) || 0) === 0 && seedOverrides.requiredReps > 0) {
      seedInput.repeat = seedOverrides.requiredReps
      inputChanged = true
    }
    if ((parseInt(input.hold, 10) || 0) === 0 && seedOverrides.requiredHoldSeconds > 0) {
      seedInput.hold = seedOverrides.requiredHoldSeconds
      seedInput.holdPostfix = 'second'
      inputChanged = true
    }

    if (overridesChanged) setOverrides(seedOverrides)
    if (inputChanged) setInput(seedInput)
    // mount only — initialInput/initialOverrides are snapshot props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const masterConfig = props.masterConfig || {}
  const resolved = (field) =>
    overrides[field] !== undefined && overrides[field] !== null
      ? overrides[field]
      : masterConfig[field]

  // REP MILESTONE editor always shows the full KOA horizon: 14 rows for
  // Weeks 0..13. Patients past the last configured week fall back to the
  // master angles in the patient session.
  const repMilestoneNumWeeks = 14

  const exerciseName =
    (props.settings && props.settings.exercise && props.settings.exercise.name) ||
    'TracPatch Exercise'

  // Currently-selected REP MILESTONE week. The patient preview overlays
  // this week's per-week angle overrides so the clinician sees the gate
  // the patient will hit during that week. Defaults to the patient's
  // actual current week computed from surgery date — Pre-Op (-1) if today
  // is before surgery, clamped to the last week (13) once past it. Falls
  // back to Week 0 when no surgery date is available.
  const initialWeek = currentWeekFromSurgery(props.surgeryDate, repMilestoneNumWeeks - 1)
  const [selectedWeek, setSelectedWeek] = useState(initialWeek)

  // Per-week resolver — per-week row wins, else the same tiered defaults
  // the override form uses for its table cells. Without this fallback the
  // preview bands would lock to the master values while the table values
  // step through tier 1/2/3 defaults, so the bands would look fixed even
  // as the highlighted week changed.
  const weekAngle = (field) =>
    resolveWeekCell(selectedWeek, field, {
      repMilestoneByWeek: overrides.rep_milestone_by_week,
      resolved,
      exerciseName,
      tierDefaults: masterConfig.tierDefaults,
      clinicRepMilestoneByWeek: masterConfig.rep_milestone_by_week,
      overrides,
    })

  // Build the resolved config used for the preview. The 4 editable inner
  // angles come from the selected week's row; the hard limits and other
  // fields come from the master/patient-level resolved values.
  const previewCfg = {
    extension_limit: resolved('extension_limit'),
    extension_low: weekAngle('extension_low'),
    extension_high: weekAngle('extension_high'),
    flexion_low: weekAngle('flexion_low'),
    flexion_high: weekAngle('flexion_high'),
    flexion_limit: resolved('flexion_limit'),
    bandsRequired: resolved('bandsRequired') || [],
    figureMode: resolved('figureMode'),
    restAngle: resolved('restAngle') != null ? resolved('restAngle') : 90,
  }
  const restAngle = previewCfg.restAngle
  const [previewAngle, setPreviewAngle] = useState(restAngle)
  // For sit-to-stand pass the config straight through — its band layout is
  // figureMode-driven (not bandsRequired-driven). For all other exercises
  // force bandsRequired: ['ext', 'flex'] so the preview shows the full ROM
  // context (both sides' bands) even when the exercise itself is one-sided;
  // the tab indicator below still tracks the real bandsRequired.
  const bands = (() => {
    try {
      if (previewCfg.figureMode === 'sit_to_stand') return deriveBands(previewCfg)
      return deriveBands({ ...previewCfg, bandsRequired: ['ext', 'flex'] })
    } catch (e) { return null }
  })()

  const handleOk = () => {
    // Guard against a stale-enabled OK (e.g. Enter key): never persist an
    // invalid or incomplete config. The override form surfaces the reason.
    if (!formValid) return
    // Force time=1 on save — TracPatch is single-slot system-wide.
    const sanitized = Object.assign({}, input, { time: 1 })
    console.log('[tracpatch][modal] handleOk', { rep_milestone_by_week: overrides.rep_milestone_by_week, overrides })
    props.onSave(sanitized, overrides)
  }

  const sliderMin = previewCfg.extension_limit != null ? previewCfg.extension_limit : -15
  const sliderMax = previewCfg.flexion_limit != null ? previewCfg.flexion_limit : 175

  // Reps for every week of the table = this schedule row's REPEAT (the care
  // plan's value for this patient). The form shows it read-only.
  const repeatNum = parseInt(input.repeat, 10)
  const repsByWeek = {}
  ;[-1, ...Array.from({ length: repMilestoneNumWeeks }, (_, i) => i)].forEach((w) => {
    repsByWeek[w] = repeatNum > 0 ? repeatNum : null
  })

  return (
    <Modal
      visible={props.visible}
      title={`Configure: ${exerciseName}`}
      onCancel={props.onCancel}
      onOk={handleOk}
      okText='Save'
      cancelText='Cancel'
      okButtonProps={{ disabled: !formValid }}
      width='75%'
      destroyOnClose
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <Row gutter={16} style={{ flexWrap: 'nowrap' }}>
        <Col flex='1 1 0' style={{ minWidth: 0 }}>
          {/* Reps per set is the schedule row's REPEAT — the care plan's value
              for this patient — and governs the patient's rep target
              (2026-10-16). Sets lives in TARGETS, Hold in TARGETS; daily
              times stays pinned to 1 system-wide for TracPatch exercises. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              marginBottom: 12,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#fff',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
              REPS PER SET
            </span>
            <InputNumber
              min={1}
              value={repeatNum > 0 ? repeatNum : null}
              onChange={(v) => setField('repeat', v == null ? '' : String(v))}
              style={{ width: 80 }}
            />
            <span style={{ fontSize: 11, color: '#888' }}>
              The care plan&apos;s reps for this exercise; applies to every week
              of this date window and is shown read-only in the table below.
            </span>
          </div>
          <TracPatchOverrideForm
            masterConfig={masterConfig}
            overrides={overrides}
            edit={true}
            onChange={handleOverridesChange}
            onValidityChange={setFormValid}
            numWeeks={repMilestoneNumWeeks}
            selectedWeek={selectedWeek}
            onSelectWeek={setSelectedWeek}
            sets={input.set}
            onSetsChange={(v) => setField('set', v)}
            exerciseName={exerciseName}
            repsByWeek={repsByWeek}
            repsSource="this patient's schedule (Reps per set above)"
          />
        </Col>

        <Col flex='0 0 340px' style={{ alignSelf: 'flex-start', position: 'sticky', top: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>
              PATIENT PREVIEW
            </div>
            <div style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>
              {selectedWeek === -1 ? 'Pre-Op' : `Week ${selectedWeek}`}
            </div>
          </div>
          <div
            style={{
              background: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              padding: 0,
              fontFamily: 'Roboto, sans-serif',
              overflow: 'hidden',
            }}
          >
            {/* Figure — PNG image-swap when the exercise has an asset
                kind (same renderer the patient app uses); fall back to
                the procedural SVG figure otherwise. */}
            <div style={{ padding: '14px 18px 6px' }}>
              {(() => {
                const assetKind = assetKindForExercise({ name: exerciseName })
                if (assetKind) {
                  return (
                    <ImageSwapFigure
                      assetKind={assetKind}
                      liveAngle={previewAngle}
                      bandRanges={bands}
                      maxWidth={240}
                      posture={resolved('posture')}
                    />
                  )
                }
                return (
                  <KneeROMFigure
                    liveAngle={previewAngle}
                    bandRanges={bands}
                    showTrackingPath={false}
                    noClamp={true}
                    maxWidth={240}
                    standUpMode={resolved('figureMode') === 'sit_to_stand'}
                    showTickIndicator={true}
                  />
                )
              })()}
            </div>

            {/* Slider */}
            <div
              style={{
                margin: '0 14px 10px',
                padding: '8px 10px',
                background: '#fff8e1',
                border: '1px solid #ffe9a8',
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#8a6d3b',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                <span>PREVIEW ANGLE</span>
                <span>{Math.round(previewAngle)}°</span>
              </div>
              <input
                type='range'
                min={sliderMin}
                max={sliderMax}
                step={1}
                value={previewAngle}
                onChange={(e) => setPreviewAngle(parseFloat(e.target.value))}
                style={{ width: '100%', transform: 'scaleX(-1)' }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#a08850',
                  marginTop: 2,
                }}
              >
                <span>← flexion ({sliderMax}°)</span>
                <span>rest ({restAngle}°)</span>
                <span>extension ({sliderMin}°) →</span>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 11, color: '#888', marginTop: 8, lineHeight: 1.5 }}>
            Mode: <b>{resolved('mode') || '—'}</b> · Posture:{' '}
            <b>{resolved('posture') || '—'}</b>
            <br />
            Reps ({selectedWeek === -1 ? 'pre-op' : `week ${selectedWeek}`}): <b>{weekAngle('reps') || '—'}</b> · Sets:{' '}
            <b>{input.set != null ? input.set : (masterConfig.requiredSets || '—')}</b>
            <br />
            Hold:{' '}
            <b>{resolved('requiredHoldSeconds') != null ? `${resolved('requiredHoldSeconds')} s` : '—'}</b>
          </div>
        </Col>
      </Row>
    </Modal>
  )
}

TracPatchConfigModal.propTypes = {
  visible: PropTypes.bool,
  settings: PropTypes.object,
  masterConfig: PropTypes.object,
  initialInput: PropTypes.object,
  initialOverrides: PropTypes.object,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  // Patient's surgery date — seeds the REP MILESTONE highlight to the
  // patient's current post-op week (Pre-Op if surgery is in the future,
  // clamped to Week 13 if more than 13 weeks past).
  surgeryDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
}

export default TracPatchConfigModal
